import type { ReactNode, SyntheticEvent } from 'react';
import { memo } from 'react';
import type { WrapperFC } from '@george-labs.com/design-system';
import { Button, StripeHeader } from '@george-labs.com/design-system';
import type { COLOR } from '@george-labs.com/design-system/types/utilsTS/color';
import cx from 'classnames';

import { t } from '@luna/i18n';
import { shouldUseEmbeddedLayout } from '@shared/core-ui-layout';
import { getFeatureConfig } from '@luna/feature-config';

import { FocusPageStripeCenter } from '../focus-page/FocusPageStripeCenter';
import style from './DetailPage.module.scss';

interface BackButtonProps {
    backLabel?: string;
    backHref?: string;
    onBackClick?: (event: SyntheticEvent) => void;
}

export interface Props extends BackButtonProps {
    left?: ReactNode;
    right?: ReactNode;
    titleDataCy?: string;
    title: string | ReactNode;
    titleOverline?: string;
    color?: COLOR;
    fullWidth?: boolean;
}

export const DetailPageStripe: WrapperFC<Props> = memo<Props>(function DetailPageStripe({
    left,
    right,
    titleDataCy = 'detail-page-title',
    title,
    titleOverline,
    color,
    backLabel = t('app.back'),
    backHref,
    onBackClick,
    fullWidth = false,
}) {
    const stripeColor = getFeatureConfig((config) => config.base.defaultStripeColor);

    return (
        <StripeHeader
            fullWidth={fullWidth}
            left={
                left ?? (
                    <Button
                        data-cy="detail-page-back-btn"
                        icon={Button.ICON.CARET_LEFT}
                        href={backHref}
                        onClick={onBackClick}
                        variant={Button.VARIANT.TERTIARY}
                        size={Button.SIZE.SMALL}
                    >
                        {backLabel}
                    </Button>
                )
            }
            center={
                <FocusPageStripeCenter data-cy={titleDataCy} overline={titleOverline}>
                    {title}
                </FocusPageStripeCenter>
            }
            right={right}
            sticky
            color={color ?? stripeColor}
            className={cx(
                style.stripeHeader,
                'detail-stripe-header',
                shouldUseEmbeddedLayout() && style.embeddedLayout,
            )}
            id="detail-stripe-header"
        />
    );
});

DetailPageStripe.wrapsComponent = StripeHeader;
